// Cart breadcrumbs for the customer ordering page (customer-entry.tsx).
//
// Gomes, 2026-10-25: Colemans #7973 was created with 3 lines; the customer says more were
// in the cart. The server saw one create and no overwrite, so the lines were lost on the
// device before the submit — and nothing recorded how (session replay is off, the cart
// logged nothing). This module records the cart's history as Sentry breadcrumbs and ships
// it with ONE info event per successful submit, so the next short order explains itself.
//
// PRIVACY: a breadcrumb or the submit context carries ids, quantities, counts, the cart's
// date key and fixed trigger names — NEVER a product or account name, phone, price, note
// or token. `cartData` drops any key that is not on the allowlist below, so a careless
// caller cannot widen it; tests/client/cart-breadcrumbs.test.ts pins it.
//
// Breadcrumbs leave the device only attached to an event, and Sentry keeps the last 100
// (its hard cap). So the seed and the submitted lines also ride in the `cart` context,
// which survives however many edits came between.
import * as Sentry from "@sentry/react";

/** Share of successful submits that send the info event. One place to turn it down if
 * Sentry quota becomes a concern (Jon, 2026-10-26: one per submit is fine). */
export const SUBMIT_EVENT_SAMPLE_RATE = 1;

export type CartTrigger =
  | "page_mount"
  | "seed"
  | "draft_restore"
  | "date_switch"
  | "new_order"
  | "discard"
  | "order_restored"
  | "account_switch"
  | "unavailable_removed"
  | "qty_add"
  | "qty_set"
  | "qty_remove"
  | "submit";

/** Where the cart's lines came from when it was seeded or re-bound. */
export type SeedSource = "draft" | "legacy_draft" | "order" | "empty";

const ALLOWED_KEYS = new Set([
  "trigger",
  "productId",
  "quantity",
  "previousQuantity",
  "lineCount",
  "totalQuantity",
  "dateKey",
  "source",
  "orderId",
  "accountId",
  "previousAccountId",
  "initialPick",
  "carried",
  "lines",
  "remounts",
  "elapsedMs",
]);

type CartData = Record<string, string | number | boolean | string[] | null | undefined>;

/** Keep only allowlisted keys with primitive (or string-array) values. */
export function cartData(raw: CartData): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!ALLOWED_KEYS.has(k) || v === undefined) continue;
    if (Array.isArray(v)) out[k] = v.filter((s) => typeof s === "string");
    else if (v === null || ["string", "number", "boolean"].includes(typeof v)) out[k] = v;
  }
  return out;
}

/** The cart's lines from the form's quantities (`product_<id>` keys), quantity > 0 only. */
export function cartLines(quantities: Record<string, unknown> | undefined | null): Map<number, number> {
  const lines = new Map<number, number>();
  for (const [key, raw] of Object.entries(quantities ?? {})) {
    if (!key.startsWith("product_")) continue;
    const id = parseInt(key.slice("product_".length), 10);
    const qty = Number(raw ?? 0);
    if (Number.isInteger(id) && id > 0 && Number.isFinite(qty) && qty > 0) lines.set(id, qty);
  }
  return lines;
}

/** Compact, name-free form of the lines: ["<productId>:<qty>", ...], sorted by id. */
export function linesSummary(lines: Map<number, number>): string[] {
  return Array.from(lines.entries()).sort((a, b) => a[0] - b[0]).map(([id, q]) => `${id}:${q}`);
}

function totalQuantity(lines: Map<number, number>): number {
  let t = 0;
  lines.forEach((q) => { t += q; });
  return t;
}

// Mounts of the ordering page since the document loaded (a page-view). Module-level so it
// counts remounts across the component's own lifetimes — Back from the order summary
// remounts the page in the same document (the Gomes mechanism).
let mountsThisPageView = 0;
let submitsThisPageView = 0;

/** Test hook: forget the counts (a new document in the browser). */
export function resetCartTrailForTests(): void {
  mountsThisPageView = 0;
  submitsThisPageView = 0;
}

export interface SubmitInfo {
  orderId: number;
  orgSlug: string | undefined;
  accountId: number | undefined;
}

/** One ordering-page lifetime's cart history. Create once per mount (useRef). */
export class CartTrail {
  private readonly mountedAt: number;
  private readonly now: () => number;
  private lines = new Map<number, number>();
  private seedSource: SeedSource | null = null;
  private seedLines: string[] = [];
  private draftRestored = false;
  readonly remounts: number;

  constructor(now: () => number = Date.now) {
    this.now = now;
    this.mountedAt = now();
    mountsThisPageView += 1;
    this.remounts = mountsThisPageView - 1;
  }

  private crumb(trigger: CartTrigger, data: CartData, level: "info" | "warning" = "info") {
    Sentry.addBreadcrumb({
      category: "cart",
      message: `cart: ${trigger}`,
      level,
      data: cartData({ trigger, ...data }),
    });
  }

  mount(): void {
    this.crumb("page_mount", { remounts: this.remounts });
  }

  /**
   * The cart was (re)filled wholesale: the first seed, or a re-bind to another date.
   * Resets the baseline the quantity diff works from.
   */
  seeded(opts: {
    trigger: "seed" | "date_switch" | "new_order" | "discard" | "order_restored";
    source: SeedSource;
    quantities: Record<string, unknown>;
    dateKey: string;
    orderId?: number | null;
    initialPick?: "earliest" | "return_to" | "requested" | "none";
  }): void {
    this.lines = cartLines(opts.quantities);
    const lines = linesSummary(this.lines);
    if (opts.trigger === "seed") {
      this.seedSource = opts.source;
      this.seedLines = lines;
    }
    const restored = opts.source === "draft" || opts.source === "legacy_draft";
    if (restored) this.draftRestored = true;
    this.crumb(opts.trigger, {
      source: opts.source,
      lineCount: this.lines.size,
      totalQuantity: totalQuantity(this.lines),
      dateKey: opts.dateKey,
      orderId: opts.orderId ?? undefined,
      initialPick: opts.initialPick,
      lines,
    });
    if (restored) {
      this.crumb("draft_restore", { source: opts.source, lineCount: this.lines.size, dateKey: opts.dateKey });
    }
  }

  /** The active date changed but the cart was carried over (a move, or a new order with items). */
  dateCarried(dateKey: string): void {
    this.crumb("date_switch", { carried: true, lineCount: this.lines.size, dateKey });
  }

  /** The server refused lines as unorderable (OW-1); the page is about to zero them. */
  unavailableRemoved(productIds: number[], dateKey: string): void {
    for (const productId of productIds) {
      this.crumb("unavailable_removed", { productId, dateKey }, "warning");
    }
  }

  /**
   * A programmatic form.reset the page did not describe with seeded() — keep the diff's
   * baseline in step so the next edit is not reported against stale lines. No breadcrumb.
   */
  resync(quantities: Record<string, unknown>): void {
    this.lines = cartLines(quantities);
  }

  accountSwitched(previousAccountId: number, accountId: number): void {
    this.crumb("account_switch", { previousAccountId, accountId });
  }

  /**
   * A user edit to the form's quantities. Diffs against the last known lines and records
   * one breadcrumb per product whose quantity changed.
   */
  quantitiesChanged(quantities: Record<string, unknown>, dateKey: string): void {
    const next = cartLines(quantities);
    const ids = new Set<number>(Array.from(this.lines.keys()).concat(Array.from(next.keys())));
    const changes: Array<{ productId: number; from: number; to: number }> = [];
    ids.forEach((id) => {
      const from = this.lines.get(id) ?? 0;
      const to = next.get(id) ?? 0;
      if (from !== to) changes.push({ productId: id, from, to });
    });
    this.lines = next;
    for (const c of changes) {
      const trigger: CartTrigger = c.from === 0 ? "qty_add" : c.to === 0 ? "qty_remove" : "qty_set";
      this.crumb(trigger, {
        productId: c.productId,
        quantity: c.to,
        previousQuantity: c.from,
        lineCount: next.size,
        dateKey,
      });
    }
  }

  /** Before the POST: a breadcrumb and the `cart` context the submit event will carry. */
  submitting(items: Array<{ productId: number; quantity: number }>, dateKey: string): void {
    const lines = new Map<number, number>();
    for (const i of items) lines.set(i.productId, (lines.get(i.productId) ?? 0) + i.quantity);
    // Built from primitives this module computed — no caller-supplied field can ride along.
    Sentry.setContext("cart", {
      lineCount: lines.size,
      totalQuantity: totalQuantity(lines),
      dateKey,
      seedSource: this.seedSource ?? "none",
      draftRestored: this.draftRestored,
      elapsedMs: this.now() - this.mountedAt,
      remounts: this.remounts,
      lines: linesSummary(lines),
      seedLines: this.seedLines,
    });
    this.crumb("submit", { lineCount: lines.size, totalQuantity: totalQuantity(lines), dateKey });
  }

  /**
   * On a 201: ship the trail. Info level, never an alert.
   *
   * The message names the order and counts the submits of this page-view, and one fixed
   * fingerprint groups them all into a single issue. A fixed message would NOT do: the
   * SDK's Dedupe integration drops a message event identical to the one before it, so
   * the resubmit after Back — the Gomes journey — would never be sent (caught by a
   * real-browser probe of the built bundle, 2026-10-26).
   */
  submitted(info: SubmitInfo, random: () => number = Math.random): void {
    submitsThisPageView += 1;
    if (random() >= SUBMIT_EVENT_SAMPLE_RATE) return;
    Sentry.captureMessage(`Customer order submitted: order ${info.orderId}, submit ${submitsThisPageView}`, {
      level: "info",
      fingerprint: ["customer-order-submitted"],
      tags: { category: "order", flow: "customer_submit" },
      extra: {
        orderId: info.orderId,
        orgSlug: info.orgSlug ?? null,
        accountId: info.accountId ?? null,
        submitsThisPageView,
      },
    });
  }
}
